import {
  CalendarEvent,
  CalendarScheduleFrom,
  ChatAdjustmentChangeWrapper,
  defaultEventCategorySet,
  RichContext,
  ScheduledAction,
  ScheduledActionEventFrom,
  TaskItemForSchedule,
  TaskLabel,
} from '@demind-inc/core';
import dayjs from 'dayjs';
import {
  ACTIVITY_EVENT_PREFIX,
  AI_SCHEDULER_EVENT_PREFIX,
  ENERGY_BOOST_EVENT_PREFIX,
  isTemporalEventId,
} from './calendar.helpers';
import {
  resolveEventCategoryFromCategoryId,
  transformTaskLabelsToEventCategories,
} from './eventCategory.helpers';
import {
  CalendarEventForEfficiency,
  SchedulerAISchedulerV5StreamResultSchedule,
  transformHHmmToTargetDate,
} from '../data-access';
import { RBCEvent } from '../components';
import { ScheduleEvent, UnifiedChatResponseV3 } from '../data-access/api/science/openapi';
import { v4 as uuidv4 } from 'uuid';
import {
  SLEEP_AFTER_MIDNIGHT_HOUR,
  stampAllPlanSchedulesWithTargetDate,
  type AllPlanDatedSchedule,
} from './allPlanScheduleDate.helpers';

export const ALL_PLAN_EVENT_PREFIX = 'all_plan';

/** Same as `autoScheduleFrom.id` on temporal events from plan-my-day / plan-week. */
export const getAiScheduledActionStableId = (action: ScheduledAction): string =>
  action.id || `${AI_SCHEDULER_EVENT_PREFIX}_${action.name}_${action.startDate}`;

/** Task/routine origin for a plan result card. Display-only — week events keep
 * `autoScheduleFrom.from` as `aiScheduler` so calendar click handling stays the same. */
export type PlanResultSource = 'todo' | 'routine';

export const getPlanResultSource = (action: ScheduledAction): PlanResultSource | undefined => {
  if (action.todoFrom || action.eventFrom === 'todo') {
    return 'todo';
  }
  if (action.routineFrom || action.eventFrom === 'routine') {
    return 'routine';
  }
  return undefined;
};

/**
 * Translates the AI scheduled action to a temporal calendar event.
 * @param action - The AI scheduled action.
 * @returns The translated calendar event.
 */
export const translateAIScheduledActionToTemporalCalendarEvent = (
  action: ScheduledAction,
  options?: { taskLabels?: TaskLabel[] }
): CalendarEvent => {
  const isSyntheticOtherTaskCategory = !!action.todoFrom && action.categoryId === 'other';
  const targetCategory =
    (isSyntheticOtherTaskCategory ? undefined : resolveEventCategoryFromCategoryId(action.categoryId)) ??
    transformTaskLabelsToEventCategories(options?.taskLabels)?.[0];
  // Preserve the AI explanation if available, otherwise use default description
  const description = action.description
    ? `${action.description} [Created from Lifestack (https://lifestack.ai)].`
    : 'AI-Scheduled event, created from Lifestack (https://lifestack.ai).';
  return {
    eventId: `${AI_SCHEDULER_EVENT_PREFIX}_${action.startDate}`,
    description,
    summary: action.name,
    start: {
      date: action.startDate,
      timeZone: dayjs.tz.guess(),
    },
    end: {
      date: action.endDate,
      timeZone: dayjs.tz.guess(),
    },
    updatedDate: dayjs().toISOString(),
    createdDate: dayjs().toISOString(),
    categories: targetCategory ? [targetCategory] : [],
    scheduleFrom: 'aiScheduler',
    autoScheduleFrom: {
      id: getAiScheduledActionStableId(action),
      from: 'aiScheduler',
    },
    taskFrom: action.todoFrom,
  };
};

/**
 * Translates an AllPlan v7 API schedule item (snake_case) to a CalendarEvent.
 * The caller must supply resolved ISO start/end datetimes via startDate/endDate.
 */
export const translateAllPlanScheduledActionToCalendarEvent = (
  raw: ScheduledAction
): CalendarEvent => {
  const actionId = raw.id ?? `${ALL_PLAN_EVENT_PREFIX}_${raw.startDate}`;

  const typeLower = raw.eventFrom;
  const categoryRaw = raw.categoryId ?? '';

  const scheduleFrom: CalendarScheduleFrom =
    typeLower === 'meal'
      ? 'meal'
      : typeLower === 'sleep'
        ? 'sleep'
        : typeLower === 'workout'
          ? 'workout'
          : typeLower === 'todo'
            ? 'todo'
            : typeLower === 'activity'
              ? 'routine'
              : typeLower === 'existing_calendar'
                ? 'calendar'
                : typeLower === 'routine'
                  ? 'routine'
                  : 'aiScheduler';

  const targetCategory = resolveEventCategoryFromCategoryId(categoryRaw);

  const descriptionText = raw.description ?? null;
  const description = descriptionText
    ? `${descriptionText} [Created from Lifestack (https://lifestack.ai)].`
    : 'AI-Planned event, created from Lifestack (https://lifestack.ai).';

  const richContext = raw.richContext
    ? ['sleep', 'meal'].includes(typeLower!)
      ? ({ ...raw.richContext, signals: [] } as RichContext) // sleep and meal events shouldn't have signals ( = past data)
      : raw.richContext
    : undefined;

  const summaryPrefix = typeLower === 'meal' ? '🍽️ ' : typeLower === 'sleep' ? '😴 ' : '';

  return {
    eventId: (uuidv4() as string).replace(/-/g, ''),
    description,
    summary: `${summaryPrefix}${raw.name}`,
    start: { date: raw.startDate, timeZone: dayjs.tz.guess() },
    end: { date: raw.endDate, timeZone: dayjs.tz.guess() },
    updatedDate: dayjs().toISOString(),
    createdDate: dayjs().toISOString(),
    categories: targetCategory ? [targetCategory] : [],
    scheduleFrom,
    autoScheduleFrom: { id: actionId, from: scheduleFrom, predicted: true },
    ...(raw.todoFrom ? { taskFrom: raw.todoFrom } : {}),
    ...(raw.routineFrom ? { routineFrom: raw.routineFrom } : {}),
    ...(richContext ? { richContext } : {}),
  };
};

/**
 * Translates the AI replanned action to a calendar event.
 * @param action - The AI replanned action.
 * @returns The translated calendar event.
 */
export const translateReplannedScheduledActionToCalendarEvent = (
  action: ScheduledAction,
  options?: { taskLabels?: TaskLabel[] }
): CalendarEvent => {
  const targetCategory =
    resolveEventCategoryFromCategoryId(action.categoryId) ??
    transformTaskLabelsToEventCategories(options?.taskLabels)?.[0];
  const description = action.description
    ? `${action.description} [Created from Lifestack (https://lifestack.ai)].`
    : 'AI-Scheduled event, created from Lifestack (https://lifestack.ai).';

  const scheduleFrom = { todo: 'todo', activity: 'routine', existing_calendar: 'aiScheduler' }[
    action.eventFrom || 'existing_calendar'
  ] as CalendarScheduleFrom;
  const autoScheduleFromId =
    (scheduleFrom === 'todo' ? action.todoFrom?.taskId : action.routineFrom?.routineActivityId) ||
    action.id ||
    `${AI_SCHEDULER_EVENT_PREFIX}_${action.startDate}`;
  const replannedEventId = convertReplannedActionToEventId(action);

  return {
    eventId: replannedEventId,
    description,
    summary: action.name,
    start: {
      date: action.startDate,
      timeZone: dayjs.tz.guess(),
    },
    end: {
      date: action.endDate,
      timeZone: dayjs.tz.guess(),
    },
    updatedDate: dayjs().toISOString(),
    createdDate: dayjs().toISOString(),
    categories: targetCategory ? [targetCategory] : [],
    scheduleFrom,
    autoScheduleFrom: {
      id: autoScheduleFromId,
      from: scheduleFrom,
    },
    taskFrom: action.todoFrom,
    routineFrom: action.routineFrom,
  };
};

export const transformReplannedScheduledActionsToCalendarEvents = (
  changes: ChatAdjustmentChangeWrapper<ScheduledAction>[]
): CalendarEvent[] => {
  return changes.flatMap((change) => {
    const merged = { ...change.before, ...change.after } as Partial<ScheduledAction>;

    if (!isRenderableReplannedAction(merged)) {
      return [];
    }

    return [translateReplannedScheduledActionToCalendarEvent(merged as ScheduledAction)];
  });
};

const buildReplannedSourceOccurrenceKey = (
  type: 'task' | 'routine' | 'auto',
  id: string,
  date: string
) => {
  return `${type}:${id}:${dayjs(date).format('YYYY-MM-DD')}`;
};

const isRenderableReplannedAction = (
  action?: Partial<ScheduledAction>
): action is Partial<ScheduledAction> => {
  return !!(
    action &&
    action.actionType !== 'remove' &&
    action.startDate &&
    action.endDate &&
    action.name
  );
};

export const getReplannedSourceOccurrenceKeys = (
  changes: ChatAdjustmentChangeWrapper<ScheduledAction>[]
) => {
  const keys = new Set<string>();

  changes.forEach((change) => {
    const merged = { ...change.before, ...change.after } as Partial<ScheduledAction>;

    if (!isRenderableReplannedAction(merged)) {
      return;
    }

    const occurrenceDate = change.before?.startDate ?? change.after?.startDate;

    if (!occurrenceDate) {
      return;
    }

    const taskId = change.before?.todoFrom?.taskId ?? change.after?.todoFrom?.taskId;
    const routineId =
      change.before?.routineFrom?.routineActivityId ?? change.after?.routineFrom?.routineActivityId;
    const sourceId = change.before?.id ?? change.after?.id;

    if (taskId) {
      keys.add(buildReplannedSourceOccurrenceKey('task', taskId, occurrenceDate));
    }

    if (routineId) {
      keys.add(buildReplannedSourceOccurrenceKey('routine', routineId, occurrenceDate));
    }

    if (sourceId) {
      keys.add(buildReplannedSourceOccurrenceKey('auto', sourceId, occurrenceDate));
    }
  });

  return keys;
};

interface DraggedReplanPreviewEvent {
  eventId?: string;
  start: {
    date: string;
  };
  end: {
    date: string;
  };
}

export const convertActionToEventId = (action: ScheduledAction): string => {
  return `${AI_SCHEDULER_EVENT_PREFIX}_${action.startDate}`;
};

export const convertReplannedActionToEventId = (action: ScheduledAction): string => {
  if (action.id) {
    return `${AI_SCHEDULER_EVENT_PREFIX}_replan_${action.id}`;
  }

  const sourceId = action.todoFrom?.taskId || action.routineFrom?.routineActivityId || 'occurrence';
  const sourceType = action.todoFrom?.taskId
    ? 'task'
    : action.routineFrom?.routineActivityId
      ? 'routine'
      : 'event';

  return `${AI_SCHEDULER_EVENT_PREFIX}_${sourceType}_${sourceId}_${action.startDate}`;
};

export const updateReplannedScheduledActionsWithDraggedEvent = (
  changes: ChatAdjustmentChangeWrapper<ScheduledAction>[],
  event: DraggedReplanPreviewEvent
) => {
  if (!event.eventId) {
    return changes;
  }

  let hasUpdated = false;

  const updatedChanges = changes.map((change) => {
    const merged = { ...change.before, ...change.after } as Partial<ScheduledAction>;

    if (!merged.startDate || !merged.endDate) {
      return change;
    }

    const replannedPreviewEventId = convertReplannedActionToEventId(merged as ScheduledAction);
    if (replannedPreviewEventId !== event.eventId) {
      return change;
    }

    hasUpdated = true;

    return {
      ...change,
      after: {
        ...merged,
        startDate: event.start.date,
        endDate: event.end.date,
      },
    };
  });

  return hasUpdated ? updatedChanges : changes;
};

export const isTemporalAutoScheduledEvent = (
  eventId: string,
  targetTypes: Array<'activity' | 'energyBoost' | 'aiScheduler'>
) => {
  if (!eventId) {
    return false;
  }
  if (targetTypes.includes('activity') && eventId.includes(ACTIVITY_EVENT_PREFIX)) {
    return true;
  }
  if (targetTypes.includes('aiScheduler') && eventId.includes(AI_SCHEDULER_EVENT_PREFIX)) {
    return true;
  }
  if (targetTypes.includes('energyBoost') && eventId.includes(ENERGY_BOOST_EVENT_PREFIX)) {
    return true;
  }
  return false;
};

export const isTemporalAiSuggestedTaskEvent = (event: Partial<RBCEvent>) => {
  return event.autoScheduleFrom?.from === 'todo' && isTemporalEventId(event.eventId!);
};

export const transformCalendarEventsForEfficiency = (
  events: Partial<CalendarEvent>[]
): CalendarEventForEfficiency[] => {
  return events.map(
    (event) =>
      ({
        summary: event.summary,
        start: event.start,
        end: event.end,
        categories: event.categories,
      }) as CalendarEventForEfficiency
  );
};

export const transformHHmmToDateConsideringMidnight = (
  time: string,
  date: string,
  timezone: string
) => {
  const [hourRaw, minuteRaw] = time.split(':');
  const hour = parseInt(hourRaw, 10);
  const minute = parseInt(minuteRaw, 10);
  const isAfterMidnight = hour >= 0 && hour < SLEEP_AFTER_MIDNIGHT_HOUR;

  if (isAfterMidnight) {
    const nextDate = dayjs(date).add(1, 'day').format('YYYY-MM-DD');
    const dateStr = `${nextDate}T${hour.toString().padStart(2, '0')}:${minute
      .toString()
      .padStart(2, '0')}:00`;
    return dayjs.tz(dateStr, timezone).toDate();
  }

  const dateStr = `${date}T${hour.toString().padStart(2, '0')}:${minute
    .toString()
    .padStart(2, '0')}:00`;
  return dayjs.tz(dateStr, timezone).toDate();
};

export const transformAllPlanV7ScheduleEventsToScheduleAction = (
  result: ScheduleEvent[],
  date: string,
  originalTasks?: TaskItemForSchedule[]
): AllPlanDatedSchedule[] => {
  const schedules = result
    .filter((schedule) => {
      // Existing calendar events are context-only — never surface as suggestions.
      if (schedule.event_from === 'existing_calendar') {
        return false;
      }
      // v8 may return them as `activity` without todo/routine origins.
      if (
        schedule.event_from === 'activity' &&
        !schedule.origin_todo_id &&
        !schedule.origin_routine_activity_id
      ) {
        return false;
      }
      return true;
    })
    .map((schedule, index) => {
      if (!schedule.start_time || !schedule.end_time) {
        return [];
      }

      const startTime = schedule.start_time;
      const endTime = schedule.end_time;

      const isSleep = schedule.event_from === 'sleep';
      const timezone = dayjs.tz.guess();
      const hasDayOffsets = schedule.start_day_offset != null || schedule.end_day_offset != null;
      const startOffset = Number(schedule.start_day_offset ?? 0);
      const endOffset = Number(schedule.end_day_offset ?? 0);

      const startDate =
        isSleep && !hasDayOffsets
          ? transformHHmmToDateConsideringMidnight(startTime, date, timezone)
          : transformHHmmToTargetDate(
              startTime,
              dayjs(schedule.start_date || date, 'YYYY-MM-DD')
                .startOf('day')
                .add(startOffset, 'day')
            );

      let endDate =
        isSleep && !hasDayOffsets
          ? transformHHmmToDateConsideringMidnight(endTime, date, timezone)
          : transformHHmmToTargetDate(
              endTime,
              dayjs(schedule.end_date || date, 'YYYY-MM-DD')
                .startOf('day')
                .add(endOffset, 'day')
            );

      if (dayjs(endDate).isBefore(dayjs(startDate))) {
        endDate = dayjs(endDate).add(1, 'day').toDate();
      }

      const originalTask = originalTasks?.find((task) => task.taskId === schedule.origin_todo_id);
      // Always stamp a durable id — accept/discard decisions and calendar previews
      // key off schedule.id. Falling back to undefined forces `plan-${index}`, which
      // breaks after sort/filter/restore when the array order changes.
      const durableId =
        schedule.action_id ||
        schedule.event_id ||
        `${date}-${schedule.event_from || 'event'}-${schedule.name || 'item'}-${index}`;

      return {
        id: durableId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        name: schedule.name,
        categoryId: schedule.category ?? undefined,
        todoFrom:
          schedule.origin_todo_id && originalTask?.boardId && originalTask?.appFrom
            ? {
                taskId: schedule.origin_todo_id,
                boardId: originalTask.boardId,
                from: originalTask.appFrom,
              }
            : undefined,
        priority: originalTask?.priority,
        routineFrom: schedule.origin_routine_activity_id
          ? { routineActivityId: schedule.origin_routine_activity_id }
          : undefined,
        description: schedule.explanation ?? schedule.description ?? undefined,
        eventFrom: (schedule.event_from as ScheduledActionEventFrom) ?? undefined,
        actionType: schedule.schedule_action ?? undefined,
        richContext: (schedule.rich_context as RichContext) ?? undefined,
      } satisfies ScheduledAction;
    })
    .flat();

  return stampAllPlanSchedulesWithTargetDate(schedules, date);
};

export const transformAISchedulerV5ResultToScheduleAction = (
  result: SchedulerAISchedulerV5StreamResultSchedule[],
  date: string,
  originalTasks?: TaskItemForSchedule[]
): ScheduledAction[] => {
  return result.map((schedule) => {
    const startDate = transformHHmmToTargetDate(schedule.start_time, dayjs(date));
    let endDate = transformHHmmToTargetDate(schedule.end_time, dayjs(date));

    if (dayjs(endDate).isBefore(dayjs(startDate))) {
      endDate = dayjs(endDate).add(1, 'day').toDate();
    }

    const originalTask = originalTasks?.find((task) => task.taskId === schedule.origin_todo_id);

    return {
      id: schedule.action_id || schedule.event_id,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      name: schedule.name,
      categoryId: schedule.category,
      todoFrom:
        schedule.origin_todo_id && originalTask
          ? {
              taskId: schedule.origin_todo_id,
              boardId: originalTask.boardId,
              from: originalTask.appFrom,
            }
          : undefined,
      description: schedule.explanation,
      priority: originalTask?.priority,
      eventFrom: schedule.event_from,
    } as ScheduledAction;
  });
};

/**
 * Transforms unified chat response format to ScheduledAction array
 * @param result - The unified chat response result
 * @param originalTasks - Optional original tasks for todoFrom mapping
 * @returns Array of ScheduledAction
 */
export const transformUnifiedChatResponseToScheduleAction = (
  result: UnifiedChatResponseV3,
  originalTasks?: TaskItemForSchedule[]
): ScheduledAction[] => {
  if (!result.unified_chat_responses || result.unified_chat_responses.length === 0) {
    return [];
  }

  const unifiedResponse = result.unified_chat_responses[0];
  const date = unifiedResponse.date;
  const schedules = unifiedResponse.adjustments?.schedule || [];

  const targetDate = dayjs(
    date,
    typeof date === 'string' && date.includes('-') ? 'YYYY-MM-DD' : 'YYYYMMDD'
  ).startOf('day');

  return schedules
    // 'existing_calendar' entries are usually unchanged context (included so
    // the AI's explanation reads coherently) and shouldn't become new
    // scheduled actions — but a schedule_action means this one IS an actual
    // add/update/remove instruction, not just grounding context, so it must
    // still go through (matches transformResultJsonSchedulesToSuggestedEvents,
    // which never filters by event_from, for the equivalent plan-my-day path).
    .filter((schedule) => schedule.event_from !== 'existing_calendar' || !!schedule.schedule_action)
    .map((schedule) => {
      const startOffset = Number(schedule.start_day_offset ?? 0);
      const endOffset = Number(schedule.end_day_offset ?? 0);
      const startTargetDate = targetDate.add(startOffset, 'day');
      const endTargetDate = targetDate.add(endOffset, 'day');

      const startDate = schedule.start_time
        ? transformHHmmToTargetDate(schedule.start_time, startTargetDate)
        : undefined;
      let endDate = schedule.end_time
        ? transformHHmmToTargetDate(schedule.end_time, endTargetDate)
        : undefined;

      if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate))) {
        endDate = dayjs(endDate).add(1, 'day').toDate();
      }

      const originalTask = originalTasks?.find((task) => task.taskId === schedule.origin_todo_id);

      return {
        id: schedule.action_id || schedule.event_id!,
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
        name: schedule.name,
        categoryId: schedule.category,
        todoFrom:
          schedule.origin_todo_id && originalTask
            ? {
                taskId: schedule.origin_todo_id,
                boardId: originalTask.boardId,
                from: originalTask.appFrom,
              }
            : undefined,
        description: schedule.explanation || schedule.description || undefined,
        eventFrom: schedule.event_from,
        actionType: schedule.schedule_action,
      } as unknown as ScheduledAction;
    });
};
